import { breakPolicyRuleSchema } from "@clipboard-health/contract-backend-main";
import { put } from "@src/appV2/api";
import { getErrorMessage } from "@src/appV2/api/errors";
import { environmentConfig } from "@src/appV2/environment";
import { pendingSettingsSchema } from "@src/appV2/FacilityDrawer/BillingAlerts/types";
import { useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { z } from "zod";

const useUpdateFacilityProfileRequestSchema = z.object({
  maxAllowedWorkHoursPerWeek: z.number().nullish(),
  maxAllowedWorkConsecutiveHours: z.number().nullish(),
  pendingSettings: pendingSettingsSchema.nullish(),
  facilityId: z.string(),
  rates: z.record(z.string(), z.number()).optional(),
  independentContractorBadge: z.object({ enabled: z.boolean() }).optional(),
  variableCharge: z.object({ enabled: z.boolean() }).optional(),
  performedBy: z.string().optional(),
  mapleIntegrationEnabled: z.boolean().optional(),
  rateNegotiation: z.string().optional(),
  requiresLunchBreak: z.boolean().optional(),
  requiresLunchBreakSince: z.string().nullish(),
  restrictOverworkShiftInvites: z.boolean().optional(),
  providesRestBreaks: z.boolean().optional(),
  providesRestBreaksSince: z.string().nullish(),
  extraWorkedTimeConfig: z
    .object({
      BREAK: z
        .object({
          isAutoDeclinationEnabled: z.boolean().optional(),
          autoDeclinationReason: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
  breakPolicies: z.array(breakPolicyRuleSchema).optional(),
  autoVerifyThresholdInMinutes: z.number().int().min(0).max(30).optional(),
});

type UseUpdateFacilityProfileRequest = z.infer<typeof useUpdateFacilityProfileRequestSchema>;

const useUpdateFacilityProfileResponseSchema = z.object({
  _id: z.string(),
  maxAllowedWorkHoursPerWeek: z.number().nullish(),
  maxAllowedWorkConsecutiveHours: z.number().nullish(),
  pendingSettings: pendingSettingsSchema.nullish(),
});

type UseUpdateFacilityProfileResponse = z.infer<typeof useUpdateFacilityProfileResponseSchema>;

export function useUpdateFacilityProfile(
  options: UseMutationOptions<
    UseUpdateFacilityProfileResponse,
    string,
    UseUpdateFacilityProfileRequest
  > = {}
) {
  const { showErrorToast } = useToast();

  return useMutation({
    mutationFn: async (params) => {
      const response = await put({
        url: `${environmentConfig.REACT_APP_API_MAIN_URL}/api/facilityprofile/put`,
        data: params,
        requestSchema: useUpdateFacilityProfileRequestSchema,
        responseSchema: useUpdateFacilityProfileResponseSchema,
      });

      return response.data;
    },
    onError: (error, variables) => {
      logError(APP_V2_APP_EVENTS.UPDATE_FACILITY_PROFILE_REJECTED, {
        error,
        metadata: { facilityId: variables.facilityId },
      });
      showErrorToast(
        getErrorMessage(
          error,
          "Error updating workplace's profile. Please, try again later or contact support."
        )
      );
    },
    ...options,
  });
}
